/* eslint-disable react/no-array-index-key,max-lines,react/no-unused-prop-types */
import moment from 'moment';
import * as React from 'react';
import { Store } from 'redux';
import * as shortId from 'shortid';
import { IMessage, State, RootState, INotificationMessage, MessageState } from './client-reducer';
import Scrollbar from './Scrollbar';
import VCCLogo from './VCCLogo';
import CloseIcon from './CloseIcon';
import ChatProviderInfo from './ChatProviderInfo';
import { getLang, getTextDirection } from './i18n';
import { VCCSpinner } from './Spinner';

const ui = getLang(navigator.language);
const textDirection = getTextDirection(navigator.language);

export interface ChatWindowActions {
    sendMessage(message: IMessage): void;
    setMessageState(message: IMessage, state: MessageState): void;
    open(): void;
    opened(): void;
    close(): void;
    closed(): void;
    startNewSession(): void;
    selectQueue(id: number): void;
}

export interface ChatWindowProps extends ChatWindowActions, State {
    store: Store<RootState>;
    transition: string;
}

interface LogoProps {
    url?: string;
    color?: string;
}

function HeaderLogo({ url, color = '#FFF' }: LogoProps) {
    return url ? <img alt="logo" src={url}/> : <VCCLogo className="logo" color={color}/>;
}

export default class ChatWindow extends React.Component<ChatWindowProps> {
    relativeDateUpdater: number | null = null;
    messageFailedTimer: number | null = null;
    input: HTMLInputElement | null = null;

    render() {
        const { backgroundColor, textColor, headerLogoUrl } = this.props.settings;

        return (
            <div className={`chatWindow ${(this.props.transition || '')} lang-${navigator.language}`}>
                <header style={{ color: textColor, background: backgroundColor }}>
                    <HeaderLogo url={headerLogoUrl} color={textColor}/>
                    <div className="icons" onClick={this.props.close}>
                        <CloseIcon color={textColor}/>
                    </div>
                </header>
                {this.renderContent()}
            </div>
        );
    }

    getInput() {
        const { backgroundColor } = this.props.settings;

        if (this.props.connected && !this.props.finished) {
            return (
                <form onSubmit={this.send}>
                    <input
                        type="text"
                        dir={textDirection}
                        ref={(ref) => {
                            this.input = ref;
                        }}
                        placeholder={ui.placeholder}
                        defaultValue=""
                    />
                    <div className="messageSendButtonContainer">
                        <button className="messageSendButton" type="submit">
                            ⮕
                        </button>
                    </div>
                </form>
            );
        }
        return (
            <div className="finished">
                <button
                    className="newSession"
                    style={{ color: backgroundColor }}
                    onClick={this.startNewSession}
                    type="button"
                >
                    <bdi>{ui.newSession}</bdi>
                </button>
            </div>
        );
    }

    renderContent() {
        const showErrorMessage = this.props.errorMessage;
        const showSpinner = !this.props.connected;
        const showQueues = this.props.queueId === null;

        if (showErrorMessage) {
            return <h3>{ui.error}</h3>;
        }
        if (showSpinner) {
            return <VCCSpinner/>;
        }
        if (showQueues) {
            if (this.props.queues.length === 0) {
                return (
                    <div className="noActiveQueueMessage">
                        <bdi>{this.props.settings.noAgentMessage}</bdi>
                    </div>
                );
            }
            return (
                <div className="queueSelectorWindow">
                    {this.props.queues.length > 1 && (
                        <div className="queueSelectionMessage">
                            <label style={{ color: this.props.settings.backgroundColor }}>
                                <bdi>{decorate(this.props.settings.queueSelectionMessage)}</bdi>
                            </label>
                        </div>
                    )}
                    <div className="queueSelectorQueuesContainer">
                        <QueueList queues={this.props.queues} onSelect={this.props.selectQueue}/>
                    </div>
                    <ChatProviderInfo/>
                </div>
            );
        }

        return (
            <div className="chatContainer">
                {this.props.messages.length === 0
                    && (
                        <div className="welcomeMessage">
                            <label>{decorate(this.props.settings.welcomeMessage)}</label>
                        </div>
                    )}
                <MessageList notificationColor={this.props.settings.backgroundColor} messages={this.props.messages}/>
                {this.getInput()}
                <ChatProviderInfo classNames="messaging"/>
            </div>
        );
    }

    componentDidMount() {
        this.relativeDateUpdater = window.setInterval(() => {
            this.forceUpdate();
        }, 30000);
    }

    componentDidUpdate() {
        if (this.props.transition === '' && this.input) {
            this.input.focus();
        }
    }

    componentWillUnmount() {
        if (this.relativeDateUpdater) {
            window.clearInterval(this.relativeDateUpdater);
        }
        if (this.messageFailedTimer) {
            window.clearInterval(this.messageFailedTimer);
        }
    }

    startNewSession = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        window.location.reload();
        this.props.startNewSession();
    };

    send = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!this.input || this.input.value === '') {
            return;
        }
        const message: IMessage = {
            id: shortId.generate(),
            from: 'webchat-plugin/teszt',
            date: new Date(),
            direction: 'out',
            message: `${this.input.value}`,
            state: 'sending',
        };
        this.messageFailedTimer = window.setTimeout(() => {
            this.props.setMessageState(message, 'failed');
        }, 5000);
        this.props.sendMessage(message);
        this.input.value = '';
    };
}
type AnyMessage = IMessage | INotificationMessage;

function QueueList({ queues, onSelect }: { queues: ChatQueue[]; onSelect(id: number): void }) {
    return (
        <div className="queueSelectorList">
            {queues.map(q => (
                <div
                    key={q.queueId}
                    className="queueSelectorListItem"
                    onClick={() => onSelect(q.queueId)}
                >
                    <div className="queueSelectorListItemContent">
                        <bdi>{q.params.label || q.name}</bdi>
                    </div>
                </div>
            ))}
        </div>
    );
}

function MessageList({ messages, notificationColor }: { messages: AnyMessage[]; notificationColor: string }) {
    return (
        <div className="message-list">
            <Scrollbar scrollBottom>
                {messages.map((m, i) => {
                    if (m.hasOwnProperty('id')) {
                        return <Message key={i} {...(m as IMessage)}/>;
                    }
                    return <NotificationMessage color={notificationColor} key={i} {...(m as INotificationMessage)}/>;
                })}
            </Scrollbar>
        </div>
    );
}

function Message({ direction, message, date, state }: IMessage): JSX.Element {
    return (
        <div className={`message dir-${direction}`}>
            <div>
                <div className="messageText">
                    <bdi>{decorate(message)}</bdi>
                </div>
                <div className="date">
                    {messageState(date, state)}
                </div>
            </div>
        </div>
    );
}

function messageState(date: Date, state?: string) {
    if (state === 'sending') {
        return <bdi>{ui.sending}</bdi>;
    }
    if (state === 'failed') {
        return <bdi className="failure">{ui.notDelivered}</bdi>;
    }
    return fromNow(date);
}

function NotificationMessage({ color, message }: INotificationMessage & { color: string }) {
    return (
        <div className="notificationMessage">
            <bdi style={{ color }}>{decorate(message)}</bdi>
        </div>
    );
}

function decorate(message: string) {
    const result: React.ReactChild[] = [];
    const isLink = /^https?:\/\/.+$/;
    const regexp = /(\s+|\S+)/g;
    let match;
    // eslint-disable-next-line no-cond-assign
    while ((match = regexp.exec(message)) !== null) {
        if (isLink.test(match[0])) {
            result.push(<a href={match[1]} title={match[1]} target="_blank" rel="noopener noreferrer">{match[1]}</a>);
        } else {
            result.push(replaceEscapeChars(match[0]));
        }
    }

    return result;
}

function fromNow(date: Date) {
    return (
        <bdi className="fromNow" title={moment(date).format()}>{moment(date).fromNow()}</bdi>
    );
}

function replaceEscapeChars(content: string) {
    return /\n/.test(content) ? <br/> : content;
}
